import React from 'react';

import { StyledTooltip } from '@DesignSystem/data-display';
import {
  DeepSpeedIcon,
  ExternalPageIcon,
  HuggingFaceIcon,
  InfoOutlinedIcon,
  KerasIcon,
  PythonIcon,
  PytorchIcon,
  PytorchLightningIcon,
  RayTrainIcon,
  ScikitLearnIcon,
  XGBoostIcon
} from '@Icons-outdated';

import styles from './QuickStartGuide.module.scss';

export const DefaultImportSection = (
  <p>
    Import comet_ml at the top of your code and define your Experiment Class.{' '}
    <StyledTooltip
      title="An Experiment automatically logs script output, code,
and command-line arguments and, for the supported
 frameworks, also logs hyperparameters, metrics, and
 model configuration."
      placement="top"
    >
      <span className={styles.infoIconContainer}>
        <InfoOutlinedIcon />
      </span>
    </StyledTooltip>
  </p>
);

export const CODE_FRAMEWORKS = {
  PYTORCH: {
    value: 'pytorch',
    label: 'Pytorch',
    prefixIcon: PytorchIcon,
    snippetFileName: `Pytorch_example.py`,
    codeSnippetLink:
      'https://github.com/comet-ml/comet-examples/tree/master/integrations/model-training/pytorch/pytorch-rich-logging',
    colabLink: `https://colab.research.google.com/github/comet-ml/comet-examples/blob/master/integrations/model-training/pytorch/notebooks/Comet_and_Pytorch.ipynb`,
    docLink: 'integrations/ml-frameworks/pytorch/'
  },
  PYTORCH_LIGHTNING: {
    value: 'pytorch-lightning',
    label: 'Pytorch Lightning',
    prefixIcon: PytorchLightningIcon,
    snippetFileName: `Pytorch_lightning_example.py`,
    codeSnippetLink:
      'https://github.com/comet-ml/comet-examples/tree/master/integrations/model-training/pytorch-lightning/pytorch-lightning-hello-world',
    colabLink: `https://colab.research.google.com/github/comet-ml/comet-examples/blob/master/integrations/model-training/pytorch-lightning/notebooks/Comet_and_Pytorch_Lightning.ipynb`,
    docLink: 'integrations/ml-frameworks/pytorch-lightning/',
    importSection: (
      <p>
        Import comet_ml and at the top of your code along with the CometLogger
        from pytorch_lightning.
      </p>
    )
  },
  KERAS: {
    value: 'keras',
    label: 'Keras',
    prefixIcon: KerasIcon,
    snippetFileName: `Keras_example.py`,
    codeSnippetLink:
      'https://github.com/comet-ml/comet-examples/tree/master/integrations/model-training/keras/keras-mnist-dnn',
    colabLink: `https://colab.research.google.com/github/comet-ml/comet-examples/blob/master/integrations/model-training/keras/notebooks/Comet_with_Keras.ipynb`,
    docLink: 'integrations/ml-frameworks/keras/',
    importSection: (
      <div>
        {DefaultImportSection}
        <p>
          Once an Experiment is created, Comet will auto-log metrics,
          hyperparams, model graph and more.
        </p>
      </div>
    )
  },
  SCIKIT_LEARN: {
    value: 'scikit-learn',
    label: 'Scikit-learn',
    prefixIcon: ScikitLearnIcon,
    snippetFileName: `Scikit-learn_example.py`,
    codeSnippetLink:
      'https://github.com/comet-ml/comet-examples/tree/master/integrations/model-training/scikit-learn/sklearn-classification-example',
    colabLink: `https://colab.research.google.com/github/comet-ml/comet-examples/blob/master/integrations/model-training/scikit-learn/notebooks/Comet_with_Scikit_Learn.ipynb`,
    docLink: 'integrations/ml-frameworks/scikit-learn/',
    importSection: (
      <div>
        {DefaultImportSection}
        <p>
          Our scikit-learn integration will automatically log all your
          hyperparameters.
        </p>
      </div>
    )
  },
  TRANSFORMERS: {
    value: 'transformers',
    label: 'Transformers',
    prefixIcon: HuggingFaceIcon,
    snippetFileName: `Transformers_example.py`,
    codeSnippetLink:
      'https://github.com/comet-ml/comet-examples/tree/master/integrations/model-training/transformers/transformers-google-bert-fine-tuning',
    colabLink: `https://colab.research.google.com/github/comet-ml/comet-examples/blob/master/integrations/model-training/transformers/notebooks/Comet_with_Hugging_Face_Trainer.ipynb`,
    docLink: 'integrations/ml-frameworks/transformers/',
    importSection: (
      <p>
        Once you have comet_ml installed, all you need to start using Comet
        alongside Transformers is to configure the Comet API Key. Hugging Face
        will create a Comet Experiment behind the scenes and log all the data.
      </p>
    )
  },
  RAY_TRAIN: {
    value: 'ray_train',
    label: 'Ray Train',
    prefixIcon: RayTrainIcon,
    snippetFileName: `Ray_train_example.py`,
    codeSnippetLink:
      'https://github.com/comet-ml/comet-examples/tree/master/integrations/model-training/ray-train/ray-train-hello-world-transformers',
    colabLink: `https://colab.research.google.com/github/comet-ml/comet-examples/blob/master/integrations/model-training/ray-train/notebooks/Comet_with_ray_train_keras.ipynb`,
    docLink: 'integrations/ml-frameworks/ray/#ray-train',
    importSection: <p>Use our callback and pass it to the Trainer:</p>,
    logSection:
      'Then from each distributed worker, use our worker context manager to get distributed system metrics logging:'
  },
  XGBoost: {
    value: 'xgboost',
    label: 'XGBoost',
    prefixIcon: XGBoostIcon,
    snippetFileName: `XGBoost_example.py`,
    codeSnippetLink:
      'https://github.com/comet-ml/comet-examples/tree/master/integrations/model-training/xgboost/xgboost-california',
    colabLink:
      'https://colab.research.google.com/github/comet-ml/comet-examples/blob/master/integrations/model-training/xgboost/notebooks/Comet_and_XGBoost.ipynb',
    docLink: 'integrations/ml-frameworks/xgboost/',
    importSection: (
      <p>
        Import comet_ml at the top of your code and define your Experiment
        Class.
      </p>
    )
  },
  DEEPSPEED: {
    value: 'DeepSpeed',
    label: 'DeepSpeed',
    prefixIcon: DeepSpeedIcon,
    snippetFileName: 'Deepspeed_example.py',
    codeSnippetLink:
      'https://github.com/comet-ml/comet-examples/tree/master/integrations/model-training/deepspeed/deepspeed-cifar',
    colabLink:
      'https://colab.research.google.com/github/comet-ml/comet-examples/blob/master/integrations/model-training/deepspeed/notebooks/comet_deepspeed.ipynb',
    docLink: 'integrations/ml-frameworks/deepspeed/',
    logSection: (
      <p>
        Add the following lines to your
        <a href="https://www.deepspeed.ai/docs/config-json/#monitoring-module:~:text=%22my_project%22%0A%7D-,comet,-%3A%20%5Bdictionary%5D">
          &nbsp; DeepSpeed config:
        </a>
      </p>
    )
  },
  PYTHON: {
    value: 'python',
    label: 'Any Python code',
    prefixIcon: PythonIcon,
    snippetFileName: `Comet_quickstart.py`,
    codeSnippetLink:
      'https://github.com/comet-ml/comet-examples/blob/master/guides/get-started/Comet_Quickstart.py',
    colabLink:
      'https://colab.research.google.com/github/comet-ml/comet-examples/blob/master/guides/get-started/Comet_Quickstart.ipynb',
    docLink: 'guides/experiment-management/log-data/overview/'
  },
  AllIntegrations: {
    value: 'AllIntegrations',
    label: 'Explore All Integrations',
    postfixIcon: ExternalPageIcon,
    docLink: '/docs/v2/integrations/overview/'
  }
};

export const CODE_FRAMEWORKS_LIST = Object.values(CODE_FRAMEWORKS);

export const getFrameworkCodeSnippet = (
  apiKey,
  projectName,
  activeWorkspaceName
) => {
  const frameworkCode = {
    [CODE_FRAMEWORKS.PYTORCH.value]: {
      IMPORT: `from comet_ml import start
from comet_ml.integration.pytorch import log_model

experiment = start(
  api_key="${apiKey}",
  project_name="${projectName}",
  workspace="${activeWorkspaceName}"
)`,
      LOGGING: `# Report multiple hyperparameters using a dictionary:\nhyper_params = {\n   "learning_rate": 0.5,\n   "steps": 100000,\n   "batch_size": 50,\n}\nexperiment.log_parameters(hyper_params)\n
# Initialize and train your model\n# model = TheModelClass()\n# train(model)\n
# Seamlessly log your Pytorch model
log_model(experiment, model=model, model_name="TheModel")\n`
    },
    [CODE_FRAMEWORKS.PYTORCH_LIGHTNING.value]: {
      IMPORT: `import comet_ml
from pytorch_lightning.loggers import CometLogger

# arguments made to CometLogger are passed to comet_ml.start
comet_logger = CometLogger(
  api_key="${apiKey}",
  project_name="${projectName}",
  workspace="${activeWorkspaceName}"
)`,
      LOGGING: `# Add CometLogger to your Trainer
trainer = Trainer(logger=comet_logger)

# Report multiple hyperparameters using a dictionary:
hyper_params = {
    "learning_rate": 0.5,
    "steps": 100000,
    "batch_size": 50,
}
comet_logger.log_hyperparams(hyper_params)`
    },
    [CODE_FRAMEWORKS.KERAS.value]: {
      IMPORT: `from comet_ml import start
experiment = start(
  api_key="${apiKey}",
  project_name="${projectName}",
  workspace="${activeWorkspaceName}"
)`,
      LOGGING: `# Report multiple hyperparameters using a dictionary:
hyper_params = {
  "learning_rate": 0.5,
  "steps": 100000,
  "batch_size": 50,
}
experiment.log_parameters(hyper_params)
    
# Or report single hyperparameters:
hidden_layer_size = 50
experiment.log_parameter("hidden_layer_size", hidden_layer_size)
    
# Long any time-series metrics:
train_accuracy = 3.14
experiment.log_metric("accuracy", train_accuracy, step=0)`
    },
    [CODE_FRAMEWORKS.SCIKIT_LEARN.value]: {
      IMPORT: `from comet_ml import start
from comet_ml.integration.sklearn import log_model

experiment = start(
  api_key="${apiKey}",
  project_name="${projectName}",
  workspace="${activeWorkspaceName}"
)`,
      LOGGING: `# Build your model and fit
# ...
# clf.fit(X_train_scaled, y_train)
    
experiment.log_parameters(params)
experiment.log_metrics(metrics)

# Seamlessly log your SKLearn model
log_model(experiment, model=clf, model_name="TheModel")`
    },
    [CODE_FRAMEWORKS.TRANSFORMERS.value]: {
      IMPORT: `import os
os.environ["COMET_API_KEY"] = "${apiKey}"`,
      LOGGING:
        'import comet_ml \n' +
        '\n' +
        'experiment = comet_ml.get_running_experiment()\n' +
        'experiment.log_metric("custom_metric", 1.0)\n'
    },
    [CODE_FRAMEWORKS.RAY_TRAIN.value]: {
      IMPORT: `import comet_ml.integration.ray

from ray.train.torch import TorchTrainer
from ray.air.config import RunConfig

callback = comet_ml.integration.ray.CometTrainLoggerCallback(
    config,
    api_key="${apiKey}",
    project_name="${projectName}",
    workspace="${activeWorkspaceName}"
)

trainer = TorchTrainer(
    train_loop_per_worker=train_func,
    train_loop_config=config,
    run_config=RunConfig(callbacks=[callback]),
)`,
      LOGGING: `def train_func(config):
    from comet_ml.integration.ray import comet_worker_logger

    with comet_worker_logger(config, api_key="${apiKey}") as experiment:
        # Your distributed training code here
        ...
        experiment.log_metric("accuracy", accuracy)`
    },
    [CODE_FRAMEWORKS.XGBoost.value]: {
      IMPORT: `from comet_ml import start
import xgboost as xgb
    
experiment = start(
  api_key="${apiKey}",
  project_name="${projectName}",
  workspace="${activeWorkspaceName}"
)`,
      LOGGING: 'model = xgb.fit()'
    },
    [CODE_FRAMEWORKS.DEEPSPEED.value]: {
      IMPORT: `"comet": {
    "enabled": True,
    "api_key": "${apiKey}",
    "project": "${projectName}",
    "workspace": "${activeWorkspaceName}",
}`,
      LOGGING:
        '"comet": {\n' +
        '    "enabled": True,\n' +
        '    "project": "comet-example-deepspeed-doc",\n' +
        '}'
    },
    [CODE_FRAMEWORKS.PYTHON.value]: {
      IMPORT: `from comet_ml import start
          
experiment = start(
  api_key="${apiKey}",
  project_name="${projectName}",
  workspace="${activeWorkspaceName}"
)`,
      LOGGING: `# Report multiple hyperparameters using a dictionary:\nhyper_params = {\n    "learning_rate": 0.5,\n    "steps": 100000,\n    "batch_size": 50,\n}\nexperiment.log_parameters(hyper_params)\n\n# Or report single hyperparameters:\nhidden_layer_size = 50\nexperiment.log_parameter("hidden_layer_size", hidden_layer_size)\n\n# Long any time-series metrics:\ntrain_accuracy = 3.14\nexperiment.log_metric("accuracy", train_accuracy, step=0)\n\n# Run your code and go to /`
    }
  };

  return frameworkCode;
};

export const CODE_SNIPPETS = {
  INSTALL: `pip install comet_ml`
};
